import React, { ReactElement, useState, useMemo } from "react";
import { makeStyles, Paper, Typography } from "@material-ui/core";
import Loader from "./Loader/Loader";

type SortDirection = "asc" | "desc";
type SortType = "string" | "number";

type SortableTableCardProps = {
  title: string;
  subtitle?: string;
  columns: Array<{
    id: string;
    label: string;
    sortType: SortType;
  }>;
  data: Array<Record<string, any>>;
  isLoading?: boolean;
  formatCell?: (
    columnId: string,
    value: any,
    row: Record<string, any>
  ) => string | ReactElement;
  defaultSortColumn?: string;
  defaultSortDirection?: SortDirection;
  sortByLabel?: string;
  noDataMessage?: string;
  style?: React.CSSProperties;
};

const useStyles = makeStyles((theme) => ({
  tableCard: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    borderRadius: 24,
    boxShadow: "0px 0px 10px 1px lightgrey",
    border: "5px solid transparent",
    maxHeight: 450,
    overflow: "hidden",
  },
  tableHeader: {
    marginBottom: theme.spacing(2),
  },
  tableTitle: {
    fontSize: "19px",
    color: "#3C3C43",
    fontWeight: 400,
    marginBottom: theme.spacing(0.5),
  },
  tableSubtitle: {
    fontSize: "16px",
    color: "#000000",
    marginTop: theme.spacing(0.5),
  },
  tableContainer: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F3F4F6",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#D1D5DB",
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: "#9CA3AF",
      },
    },
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  tableHead: {
    backgroundColor: "#FFFFFF",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  tableHeaderRow: {
    borderBottom: "2px solid #E5E7EB",
  },
  tableHeaderCell: {
    padding: theme.spacing(1, 1.5),
    textAlign: "left",
    fontSize: "16px",
    fontWeight: 600,
    color: "#000000",
    borderBottom: "2px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    position: "sticky",
    top: 0,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  sortIndicator: {
    marginLeft: theme.spacing(0.5),
    fontSize: "16px",
  },
  tableBody: {},
  tableRow: {
    "&:not(:last-child)": {
      borderBottom: "1px solid #F3F4F6",
    },
  },
  tableCell: {
    padding: theme.spacing(2, 1.5),
    fontSize: "18px",
    color: "#000000",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    fontSize: "18px",
    color: "#000000",
  },
}));

const sortData = ({
  data,
  column,
  direction,
  sortType,
}: {
  data: Array<Record<string, any>>;
  column: string;
  direction: SortDirection;
  sortType: SortType;
}): Array<Record<string, any>> => {
  return [...data].sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    if (sortType === "number") {
      const numA = typeof aVal === "number" ? aVal : 0;
      const numB = typeof bVal === "number" ? bVal : 0;
      return direction === "asc" ? numA - numB : numB - numA;
    }

    const strA = String(aVal || "");
    const strB = String(bVal || "");
    return direction === "asc"
      ? strA.localeCompare(strB)
      : strB.localeCompare(strA);
  });
};

const SortableTableCard = ({
  title,
  subtitle,
  columns,
  data,
  isLoading,
  formatCell,
  defaultSortColumn,
  defaultSortDirection = "desc",
  sortByLabel = "Sort by",
  noDataMessage = "No data available",
  style,
}: SortableTableCardProps): ReactElement => {
  const classes = useStyles();

  const [sortColumn, setSortColumn] = useState<string | null>(
    defaultSortColumn || null
  );
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);

  const handleHeaderClick = ({ columnId }: { columnId: string }) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnId);
      // For string columns, start with ascending (A-Z)
      // For number columns, start with descending (highest to lowest)
      const column = columns.find((col) => col.id === columnId);
      const initialDirection = column?.sortType === "string" ? "asc" : "desc";
      setSortDirection(initialDirection);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !data || data.length === 0) {
      return data;
    }

    const column = columns.find((col) => col.id === sortColumn);
    if (!column) {
      return data;
    }

    return sortData({
      data,
      column: sortColumn,
      direction: sortDirection,
      sortType: column.sortType,
    });
  }, [data, sortColumn, sortDirection, columns]);

  const getSortIndicator = ({ columnId }: { columnId: string }): string => {
    if (sortColumn !== columnId) {
      return "↕";
    }
    return sortDirection === "asc" ? "↑" : "↓";
  };

  return (
    <Paper elevation={0} className={classes.tableCard} style={style}>
      {!isLoading && (
        <div className={classes.tableHeader}>
          <Typography component="h3" className={classes.tableTitle}>
            {title}
          </Typography>
          {subtitle && (
            <Typography component="p" className={classes.tableSubtitle}>
              {subtitle}
            </Typography>
          )}
        </div>
      )}

      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <Loader />
        </div>
      ) : data.length === 0 ? (
        <div className={classes.emptyState}>{noDataMessage}</div>
      ) : (
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.tableHead}>
              <tr className={classes.tableHeaderRow}>
                {columns.map((column) => (
                  <th
                    key={column.id}
                    className={classes.tableHeaderCell}
                    onClick={() => handleHeaderClick({ columnId: column.id })}
                    role="button"
                    tabIndex={0}
                    aria-label={`${sortByLabel} ${column.label}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleHeaderClick({ columnId: column.id });
                      }
                    }}
                  >
                    {column.label}
                    <span className={classes.sortIndicator}>
                      {getSortIndicator({ columnId: column.id })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={classes.tableBody}>
              {sortedData.map((row, rowIndex) => (
                <tr key={rowIndex} className={classes.tableRow}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    const formattedValue = formatCell
                      ? formatCell(column.id, value, row)
                      : value;

                    return (
                      <td
                        key={`${rowIndex}-${column.id}`}
                        className={classes.tableCell}
                      >
                        {formattedValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Paper>
  );
};

export default SortableTableCard;
